import React, { useState } from 'react';
import { withRouter } from 'react-router-dom';
import { ArrowLeft, CheckCircle } from '@phosphor-icons/react';
import axiosAPI from '../../api/axiosAPI';

const OrganizationCodePage = ({ history }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [orgInfo, setOrgInfo] = useState(null);
  const [validationStep, setValidationStep] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Skip organization code validation if user wants to use VirtusAuto
    if (code.trim() === '') {
      const virtusAutoInfo = {
        code: 'VAUTO382',
        name: 'VirtusAuto',
        subscriptionAmount: '12.00',
      };

      setOrgInfo(virtusAutoInfo);
      setValidationStep(true);
      setLoading(false);
      return;
    }

    try {
      const response = await axiosAPI.get(
        `/api/v1/student/registration/validate-code/${code.trim()}`
      );

      if (response.data.valid) {
        const orgData = {
          code: code.trim(),
          name: response.data.organization_name,
          subscriptionAmount: response.data.subscription_amount,
        };

        setOrgInfo(orgData);
        setValidationStep(true);
      } else {
        setError('Código de autoescuela inválido');
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          'Error al validar el código. Por favor, inténtelo de nuevo.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    // Store VirtusAuto as the default organization
    const virtusAutoInfo = {
      code: 'VAUTO382',
      name: 'VirtusAuto',
      subscriptionAmount: '12.00',
    };

    setOrgInfo(virtusAutoInfo);
    setValidationStep(true);
  };

  const confirmOrganization = () => {
    // Store organization data in session storage for use in the registration form
    sessionStorage.setItem('registrationOrg', JSON.stringify(orgInfo));

    // Redirect to registration form
    history.push('/register/form');
  };

  const editCode = () => {
    setValidationStep(false);
    setOrgInfo(null);
  };

  return (
    <div className="flex flex-col h-screen">
      <div
        className="w-full p-8 overflow-y-auto flex items-center justify-center flex-1 bg-cover bg-bottom"
        style={{ backgroundImage: 'url(/register.jpg)' }}
      >
        <div className="w-full md:max-w-md 2xl:max-w-xl">
          <div className="bg-white rounded-xl shadow-xl transform transition-all shadow-md hover:shadow-2xl focus:shadow-2xl">
            <div className="px-4 py-4 md:px-12 md:py-12">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-16 w-16">
                <a
                  href="https://www.vrtus.app/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src="/logo_dark.jpg"
                    alt="Logo"
                    className="h-16 w-16 rounded-full object-cover cursor-pointer"
                  />
                </a>
              </div>

              {!validationStep ? (
                <div className="mt-3 text-center">
                  <div className="text-3xl font-medium text-gray-900 mb-4">
                    Únete a tu Autoescuela
                  </div>

                  <div className="text-gray-500 mb-6">
                    Introduce el código proporcionado por tu autoescuela para
                    crear una cuenta.
                  </div>

                  <form onSubmit={handleSubmit} className="w-full">
                    <div className="flex flex-col">
                      <input
                        className="rounded-md text-lg px-4 py-2 border border-gray-300 mb-4"
                        placeholder="Código de Autoescuela"
                        value={code}
                        onChange={e => setCode(e.target.value)}
                        disabled={loading}
                      />

                      <button
                        type="submit"
                        className="hover:bg-gray-600 font-medium rounded-lg text-lg px-4 py-2 bg-gray-500 text-white border border-gray-300 mb-3"
                        disabled={loading}
                      >
                        {loading ? 'Cargando...' : 'Continuar'}
                      </button>

                      <button
                        type="button"
                        onClick={handleSkip}
                        className="font-medium rounded-lg text-lg px-4 py-2 bg-white text-gray-500 border border-gray-300"
                      >
                        Continuar sin código
                      </button>
                    </div>
                  </form>

                  {error && (
                    <div className="text-red-600 bg-red-50 rounded-md p-1 text-center mt-4">
                      {error}
                    </div>
                  )}

                  <div className="mt-6 text-sm text-gray-500">
                    Si no tienes un código de autoescuela, puedes continuar sin
                    él y acceder a la formación 100% online con VirtusAuto.
                  </div>

                  <div className="mt-3 text-center text-sm">
                    <span className="text-gray-500">
                      ¿Ya tienes una cuenta?{' '}
                    </span>
                    <a
                      href="/login"
                      className="text-gray-700 font-medium hover:underline"
                    >
                      Inicia sesión
                    </a>
                  </div>
                </div>
              ) : (
                <div className="mt-3 text-center">
                  <div className="text-3xl font-medium text-gray-900 mb-4">
                    Confirma tu Autoescuela
                  </div>

                  <div className="bg-green-50 rounded-lg p-4 mb-6 flex items-center">
                    <CheckCircle
                      size={32}
                      className="text-green-500 mr-3 flex-shrink-0"
                    />
                    <div className="text-left">
                      <div className="text-green-800 font-medium">
                        Autoescuela verificada
                      </div>
                      <div className="text-green-700 text-xl font-semibold">
                        {orgInfo.name}
                      </div>
                      {orgInfo.subscriptionAmount && (
                        <div className="text-green-700 mt-1">
                          Cuota: {orgInfo.subscriptionAmount}€ / mes
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <button
                      onClick={confirmOrganization}
                      className="hover:bg-gray-600 font-medium rounded-lg text-lg px-4 py-2 bg-gray-500 text-white border border-gray-300 mb-3"
                    >
                      Continuar
                    </button>

                    <button
                      onClick={editCode}
                      className="font-medium rounded-lg text-lg px-4 py-2 bg-white text-gray-500 border border-gray-300"
                    >
                      Editar código
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withRouter(OrganizationCodePage);
